import type {
    Cart,
    CartItemInterface,
    CartUserInputError,
    Maybe,
    ProductInterface
} from '@pylot-data/pylotschema'
import { CartUserInputErrorType } from '@pylot-data/pylotschema'
import type { CartData } from '../use-cart'
import {
    openGiftCardMixAlert,
    type PendingMixAddToCart
} from './giftCardMixAlert'

export const GIFT_CARD_MIX_I18N_KEY =
    'cart|Cannot mix gift cards and regular products'

export const isGiftCardLikeProduct = (
    product: Maybe<ProductInterface> | unknown
): boolean => {
    if (!product || typeof product !== 'object') {
        return false
    }
    const candidate = product as ProductInterface & {
        __typename?: string | null
        giftcard_type?: string | null
        gift_card_options?: unknown
    }
    if (candidate.__typename === 'GiftCardProduct') {
        return true
    }
    if (
        typeof candidate.giftcard_type === 'string' &&
        candidate.giftcard_type.length > 0
    ) {
        return true
    }
    return 'gift_card_options' in candidate
}

export const isGiftCardLikeCartItem = (
    item: Maybe<CartItemInterface> | unknown
): boolean => {
    if (!item || typeof item !== 'object') {
        return false
    }
    const candidate = item as CartItemInterface
    if (candidate.__typename === 'GiftCardCartItem') {
        return true
    }
    return isGiftCardLikeProduct(candidate.product)
}

export const isGiftCardPhysicalMix = (
    cart: Maybe<Pick<Cart, 'items'>> | undefined,
    products?: Maybe<ProductInterface>[] | ProductInterface[]
): boolean => {
    const adding = (products ?? []).filter(
        (product): product is ProductInterface => Boolean(product)
    )
    const addingGift = adding.some(isGiftCardLikeProduct)
    const addingPhysical = adding.some(
        (product) => !isGiftCardLikeProduct(product)
    )

    if (addingGift && addingPhysical) {
        return true
    }

    const items = (cart?.items ?? []).filter(
        (item): item is CartItemInterface => Boolean(item)
    )
    if (!items.length || !adding.length) {
        return false
    }

    const cartHasGift = items.some(isGiftCardLikeCartItem)
    const cartHasPhysical = items.some((item) => !isGiftCardLikeCartItem(item))

    return (cartHasGift && addingPhysical) || (cartHasPhysical && addingGift)
}

export const isGiftCardMixBackendMessage = (
    message?: string | null
): boolean => {
    if (!message) {
        return false
    }
    const normalized = message.toLowerCase()
    const mentionsGift = /gift\s*card|giftcard|tarjeta de regalo/.test(
        normalized
    )
    const mentionsConflict =
        /physical|f[ií]sico|same cart|mismo carrito|combin/.test(normalized)
    return mentionsGift && mentionsConflict
}

export const createGiftCardMixUserError = (): CartUserInputError => ({
    code: CartUserInputErrorType.Undefined,
    message: GIFT_CARD_MIX_I18N_KEY
})

export const isGiftCardMixCartResult = (
    result?: Pick<CartData, 'errors' | 'user_errors'> | null
): boolean => {
    if (!result) {
        return false
    }
    const messages = [
        ...(result.user_errors ?? []).map((error) => error?.message),
        ...(result.errors ?? []).map((error) => error?.message)
    ]
    return messages.some(
        (message) =>
            message === GIFT_CARD_MIX_I18N_KEY ||
            isGiftCardMixBackendMessage(message)
    )
}

export const blockGiftCardMixIfNeeded = (
    cart: Maybe<Cart> | undefined,
    products: ProductInterface[] | undefined,
    pending: PendingMixAddToCart
): CartData | undefined => {
    if (!isGiftCardPhysicalMix(cart, products)) {
        return undefined
    }
    openGiftCardMixAlert(pending)
    return {
        data: { cart: cart as Cart },
        user_errors: [createGiftCardMixUserError()]
    }
}

export const openGiftCardMixAlertIfBackendRejected = (
    pending: PendingMixAddToCart,
    messages: Array<string | null | undefined>
): boolean => {
    const isMix = messages.some(
        (message) =>
            message === GIFT_CARD_MIX_I18N_KEY ||
            isGiftCardMixBackendMessage(message)
    )
    if (!isMix) {
        return false
    }
    openGiftCardMixAlert(pending)
    return true
}
